import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostListener } from '@angular/core';
import {
  animate,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TourFeature } from 'src/app/onboarding/config/tour-feature.enum';
import { ContextualTourTriggerService } from 'src/app/onboarding/services/contextual-tour-trigger.service';
import { OnboardingChecklistFabComponent } from 'src/app/onboarding/rendering/checklist-fab/onboarding-checklist-fab.component';
import { XdsIconButtonComponent } from 'src/app/shared/ui/icon-button/icon-button.component';
import { XdsTabsComponent, XdsTab } from 'src/app/shared/ui/tabs/tabs.component';
import { XdsAvatarComponent } from 'src/app/shared/ui/avatar/avatar.component';
import { XdsBadgeComponent } from 'src/app/shared/ui/badge/badge.component';
import { HelpChatComponent } from '../help-chat/help-chat.component';
import { HelpChatService } from '../services/help-chat.service';
import { DemoAiQuota } from '../models/help-chat.models';
import { TourPreflightService } from '../services/tour-preflight.service';
import { isDemoSessionActive } from 'src/app/modules/auth/services/demo/demo-session-flags';
import { OnboardingAnchorDirective } from 'src/app/onboarding/component/onboarding/onboarding-anchor.directive';
import { HelpHubStateService, HubTab } from './help-hub-state.service';
import { PageTourButtonComponent } from '../page-tours/page-tour-button.component';

/**
 * Help-Hub: EIN Launcher unten rechts, der Chat (Fragen) und Einrichtungs-
 * Checkliste (Einrichtung) als Tabs unter einem Dach vereint. Loest den
 * frueheren Help-FAB (unten rechts) UND die Einrichtungs-Pille (unten links)
 * ab — die linke Ecke wird frei.
 */
@Component({
  selector: 'app-help-hub',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    HelpChatComponent,
    PageTourButtonComponent,
    OnboardingChecklistFabComponent,
    XdsIconButtonComponent,
    XdsTabsComponent,
    XdsAvatarComponent,
    XdsBadgeComponent,
    OnboardingAnchorDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Panel klappt beim Oeffnen sanft auf und beim Schliessen zur Launcher-Ecke
  // (unten rechts) wieder zusammen — statt hart zu verschwinden. Greift auch,
  // wenn das Panel beim Tour-Start automatisch geschlossen wird.
  animations: [
    trigger('hubPanel', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(8px) scale(0.96)' }),
        animate(
          '200ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({ opacity: 1, transform: 'none' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '180ms cubic-bezier(0.4, 0, 1, 1)',
          style({ opacity: 0, transform: 'translateY(10px) scale(0.92)' }),
        ),
      ]),
    ]),
  ],
  template: `
    <ng-container *ngIf="state.vm$ | async as vm">
     <!-- TODO-206: unterdrückt Launcher/Backdrop/Panel, solange eine Vollflächen-Surface
          (z. B. das Task-Detail-Vollbild-Sheet) den FAB verdecken würde. -->
     <ng-container *ngIf="(state.suppressed$ | async) !== true">
      <!-- Launcher -->
      <button
        type="button"
        class="hub-launcher"
        data-testid="help-hub-launcher"
        onboardingAnchor="assistant-launcher"
        [class.is-open]="state.open$ | async"
        (click)="state.toggle(vm.defaultTab)"
        [attr.aria-expanded]="(state.open$ | async) ? 'true' : 'false'"
        [attr.aria-label]="
          ((state.open$ | async)
            ? 'HELP_HUB.CLOSE'
            : (isDemo ? 'HELP_HUB.OPEN_DEMO' : 'HELP_HUB.OPEN')) | translate
        ">
        <!-- Ring nur im geschlossenen Zustand: offen ist der Launcher der
             Schließen-Schalter, ein Fortschrittsring waere dort irritierend. -->
        <svg
          *ngIf="vm.ringVisible && !(state.open$ | async)"
          class="hub-ring"
          width="56"
          height="56"
          viewBox="0 0 56 56"
          aria-hidden="true">
          <circle cx="28" cy="28" r="24" class="hub-ring-track"></circle>
          <circle
            cx="28"
            cy="28"
            r="24"
            class="hub-ring-fill"
            [attr.stroke-dasharray]="ringDash(vm.ringPct)"></circle>
        </svg>
        <!-- Icon morpht zum Schließen-Pfeil, wenn das Panel offen ist:
             EIN Toggle-Punkt (Intercom/Crisp-Pattern), der Launcher bleibt
             immer sichtbar — das Panel klappt darueber auf, verdeckt ihn nie. -->
        <span class="hub-launcher-icon" aria-hidden="true">
          <i
            class="fa-solid"
            [class.fa-life-ring]="!isDemo && !(state.open$ | async)"
            [class.fa-sparkles]="isDemo && !(state.open$ | async)"
            [class.fa-chevron-down]="state.open$ | async"></i>
        </span>
      </button>

      <!-- Backdrop: nur Touch-Tablet (CSS-gesteuert). Tap ausserhalb schliesst das Panel
           und ein dezenter Scrim hebt es von der dichten Dashboard-Flaeche ab. Auf Desktop
           (Maus) ausgeblendet -> nicht-modaler Intercom-Stil bleibt erhalten. -->
      <div
        *ngIf="state.open$ | async"
        class="hub-backdrop"
        (click)="state.close()"
        aria-hidden="true"></div>

      <!-- Panel -->
      <section
        *ngIf="state.open$ | async"
        @hubPanel
        class="hub-panel"
        data-testid="help-hub-panel"
        role="dialog"
        aria-modal="false"
        [attr.aria-label]="'HELP_HUB.TITLE' | translate">
        <!-- A2 — EINE Kopfzeile statt drei. Bis 2026-09-09 standen hier „Hilfe & Einrichtung"
             (60,8px), die Reiterleiste (42,2px) und darunter noch einmal die Kopfzeile des
             Chats („Assistent · Bereit", 52,8px) — zusammen 155,8px, bevor ein Wort Inhalt kam.
             Jetzt traegt diese Zeile die Identitaet des AKTIVEN Reiters; .help-chat__header
             entfaellt im eingebetteten Modus (help-chat.component.html).
             Auf dem Einrichtungs-Reiter bleibt die Hub-Identität stehen — ohne sie hiesse der
             Kopf „Assistent" über einer Setup-Checkliste. -->
        <header class="hub-header">
          <ng-container *ngIf="(state.activeTab$ | async) === 'fragen'; else setupIdentity">
            <xds-avatar
              class="hub-header-avatar"
              size="sm"
              icon="fa-solid fa-sparkles"
              status="ai"
              [name]="'HELP_CHAT.ASSISTANT_NAME' | translate"
            ></xds-avatar>
            <div class="hub-header-id">
              <h2 class="hub-header-title">{{ 'HELP_CHAT.ASSISTANT_NAME' | translate }}</h2>
              <span class="hub-header-status">
                <i class="fa-solid fa-circle" aria-hidden="true"></i>
                {{
                  (chat.pending() ? 'HELP_CHAT.STATUS_THINKING' : 'HELP_CHAT.STATUS_READY')
                    | translate
                }}
              </span>
            </div>
            <!-- Demo: KI-Kontingent-Badge ("KI x/10"); nur in einer Demo-Session sichtbar. -->
            <xds-badge
              *ngIf="chat.demoAiQuota() as quota"
              [variant]="quotaBadgeVariant(quota)"
              icon="fa-light fa-robot"
              >{{
                'DEMO.AI_QUOTA_BADGE'
                  | translate: { remaining: quota.remaining, quota: quota.quota }
              }}</xds-badge
            >
            <xds-icon-button
              variant="ghost"
              icon="fa-light fa-rotate-right"
              [ariaLabel]="'HELP_CHAT.NEW_CONVERSATION' | translate"
              (clicked)="state.requestNewConversation()"
            ></xds-icon-button>
          </ng-container>
          <ng-template #setupIdentity>
            <span class="hub-header-icon" aria-hidden="true"
              ><i class="fa-solid fa-life-ring"></i
            ></span>
            <h2 class="hub-header-title">{{ 'HELP_HUB.TITLE' | translate }}</h2>
          </ng-template>
          <xds-icon-button
            variant="secondary"
            icon="fa-light fa-xmark"
            [ariaLabel]="'HELP_HUB.CLOSE' | translate"
            (clicked)="state.close()"
          ></xds-icon-button>
        </header>

        <!-- Eine Reiterleiste mit genau EINEM Reiter ist keine Navigation, sondern eine
             Überschrift, die 42px kostet. Sie erscheint erst, wenn es etwas zu wechseln gibt
             (in der Demo entfällt der Einrichtungs-Reiter, siehe A5). -->
        <div class="hub-tabs-wrap" *ngIf="tabsFor(vm.showEinrichtungTab).length > 1">
          <xds-tabs
            align="stretch"
            testIdPrefix="help-hub-tab-"
            [tabs]="tabsFor(vm.showEinrichtungTab)"
            [activeId]="(state.activeTab$ | async) ?? undefined"
            (activeIdChange)="onTabChange($event)"
          ></xds-tabs>
        </div>

        <div class="hub-body">
          <!-- Fragen-Pane: bleibt immer am Leben (Konversation erhalten). -->
          <!-- Das frühere [embedded]="true" entfällt: Es gab nur noch diesen einen Aufrufer,
               und der zweite Modus (eigener MatDialog) ist entfernt. Ein Schalter mit genau
               einer möglichen Stellung ist keine Konfiguration.
               (Kein Backtick in diesem Kommentar — die Datei ist ein Inline-template:, ein
               Backtick beendet dort die Zeichenkette und bricht den Build. TS2304 am
               10.09.2026 genau so kassiert.) -->
          <app-help-chat
            class="hub-pane"
            [hidden]="(state.activeTab$ | async) !== 'fragen'"
            (requestGuide)="onGuide($event)">
            <!-- 12.10.2026: Touren der Seite als Zeile im leeren Assistenten statt als eigener
                 Reiter (09.–12.10.). Der Reiter hatte meist genau einen Eintrag, den der Knopf
                 „Tour“ im Seitenkopf ohnehin startet. Ohne Touren rendert die Zeile nichts. -->
            <app-page-tour-button
              helpChatEmptyLead
              variant="hint"
              (launching)="state.close()"></app-page-tour-button>
          </app-help-chat>

          <!-- Einrichtung-Pane: nur instanziieren, wenn der Tab sichtbar ist. -->
          <app-onboarding-checklist-fab
            *ngIf="vm.showEinrichtungTab"
            class="hub-pane"
            [embedded]="true"
            [hidden]="(state.activeTab$ | async) !== 'einrichtung'"></app-onboarding-checklist-fab>
        </div>
      </section>
     </ng-container>
    </ng-container>
  `,
  styles: [
    `
      :host {
        display: contents;
      }
      .hub-launcher {
        position: fixed;
        /* Material-FAB-Ränder: 24px auf Desktop/Tablet + Safe-Area-Insets,
           damit der FAB auf iPad/iPhone (PWA/Capacitor) nicht unter dem
           Home-Indicator / der Gesten-Leiste klebt. Mobile-Override unten. */
        right: calc(24px + env(safe-area-inset-right, 0px));
        bottom: calc(24px + env(safe-area-inset-bottom, 0px));
        z-index: 8500;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        border: none;
        background: var(--action-primary, #0284c7);
        color: var(--text-on-primary, #fff);
        cursor: pointer;
        box-shadow: 0 8px 24px rgba(2, 132, 199, 0.34), 0 2px 8px rgba(16, 24, 40, 0.14);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.15s ease, background 0.15s ease;
      }
      .hub-launcher:hover {
        transform: translateY(-1px);
        background: var(--action-primary-hover, #0369a1);
      }
      .hub-launcher.is-open {
        background: var(--action-primary-hover, #0369a1);
      }
      .hub-ring {
        position: absolute;
        inset: 0;
        /* width/height:100% statt der Attribut-Größe (56x56) — skaliert per
           viewBox proportional mit, sobald der Launcher auf Mobile kleiner
           wird (siehe @media unten), statt einen Ausschnitt zu croppen. */
        width: 100%;
        height: 100%;
        transform: rotate(-90deg);
        /* Ambient: der Ring ist ein dezenter Fortschritts-Hinweis, kein KPI.
           Die frühere permanente „%"-Zahl entfällt (Doppelung zur Onboarding-
           Karte + visuelles Rauschen). Sichtbarkeit weiterhin via vm.ringVisible
           — verschwindet, sobald das Onboarding abgeschlossen ist. */
      }
      .hub-ring-track {
        fill: none;
        stroke: rgba(255, 255, 255, 0.28);
        stroke-width: 3.5;
      }
      .hub-ring-fill {
        fill: none;
        stroke: #fff;
        stroke-width: 3.5;
        stroke-linecap: round;
        transition: stroke-dasharray 0.4s ease;
      }
      .hub-launcher-icon {
        font-size: 22px;
        line-height: 1;
      }

      .hub-backdrop {
        position: fixed;
        inset: 0;
        z-index: 8550;
        background: transparent;
        /* Desktop (Maus): nicht-modal — kein Scrim, blockiert nichts. Nur Touch-Tablets
           bekommen einen sichtbaren Tap-zum-Schliessen-Scrim (Media-Query unten). */
        display: none;
      }

      .hub-panel {
        position: fixed;
        /* Rechtskante mit dem Launcher buendig; das Panel klappt OBERHALB des
           Launchers auf (24px Rand + 56px Launcher + 12px Luft = 92px), damit
           der Launcher als persistenter Schliessen-Schalter sichtbar bleibt
           und nie verdeckt wird (Intercom/Crisp-Pattern). */
        right: calc(24px + env(safe-area-inset-right, 0px));
        bottom: calc(92px + env(safe-area-inset-bottom, 0px));
        z-index: 8600;
        width: 380px;
        max-width: calc(100vw - 32px);
        height: 70vh;
        /* Nie ueber den oberen Rand hinaus: Hoehe auf den Platz oberhalb des
           Launchers begrenzen. */
        max-height: min(640px, calc(100vh - 116px - env(safe-area-inset-bottom, 0px)));
        min-height: 420px;
        display: flex;
        flex-direction: column;
        background: var(--clr-surface-100, #fff);
        border: 1px solid var(--clr-border, #e4e8ee);
        border-radius: 14px;
        overflow: hidden;
        box-shadow: 0 14px 40px rgba(16, 24, 40, 0.16), 0 6px 16px rgba(16, 24, 40, 0.08);
        /* Zusammenklappen Richtung Launcher-Ecke (unten rechts). Auf-/Zuklapp-
           Animation kommt aus dem @hubPanel-Trigger (siehe Component-Metadaten). */
        transform-origin: bottom right;
      }
      .hub-header {
        display: flex;
        align-items: center;
        /* A2/A4: Abstände aus Tokens. --stack-gap fällt am Telefon von 16 auf 8px —
           genau die Stufe, die die Kopfzeile unter die 70px-Grenze bringt. */
        gap: var(--sp-3);
        padding: var(--stack-gap) var(--sp-4);
        border-bottom: 1px solid var(--clr-border, #e4e8ee);
        /* --tint-primary-* statt hartcodiertem Cyan-rgba: matcht dieselbe
           Farbfamilie wie der Launcher-FAB (--action-primary) statt der
           App-eigenen --clr-primary-*-Cyantoene. */
        background: linear-gradient(135deg, var(--tint-primary-bg, #f0f9ff) 0%, var(--clr-surface-100, #fff) 62%);
      }
      .hub-header-icon {
        width: 30px;
        height: 30px;
        border-radius: 9px;
        background: var(--tint-primary-bg, #f0f9ff);
        color: var(--tint-primary-text, #0369a1);
        border: 1px solid var(--tint-primary-border, #bae6fd);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: var(--fs-base);
        flex-shrink: 0;
      }
      .hub-header-avatar {
        flex: none;
      }
      /* Name + Status als Paar — nimmt den Platz, den vorher der Titel allein hatte. */
      .hub-header-id {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
      }
      .hub-header-title {
        flex: 1;
        margin: 0;
        font-size: var(--fs-md);
        font-weight: 800;
        color: var(--clr-text-100, #1d2733);
        line-height: 1.3;
      }
      .hub-header-id .hub-header-title {
        flex: none;
      }
      .hub-header-status {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        /* A4: war 10,5px roh im Chat-Kopf — kleinste Schrift der ganzen Oberfläche. */
        font-size: var(--fs-xs);
        font-weight: 600;
        color: var(--text-subtle);
      }
      /* Echter Kreis statt 6px-Glyph: ein Punkt ist kein Text und soll in einer
         Schriftgrößen-Abnahme auch nicht als solcher auftauchen. */
      .hub-header-status i {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--action-success);
        font-size: 0;
      }
      .hub-tabs-wrap {
        padding: 0 8px;
        background: var(--clr-surface-200, #f6f8fb);
      }
      .hub-tabs-wrap xds-tabs {
        display: block;
      }

      .hub-body {
        flex: 1;
        min-height: 0;
        position: relative;
        overflow: hidden;
        display: flex;
      }
      .hub-pane {
        flex: 1;
        min-width: 0;
        display: block;
        height: 100%;
        overflow: auto;
      }
      .hub-pane[hidden] {
        display: none;
      }

      @media (max-width: 600px) {
        .hub-launcher {
          /* Telefon: 16px-Rand + Safe-Area; bottom hält 80px Abstand zur
             unteren Aktionsleiste, jetzt zusätzlich Safe-Area-aware.
             Größe 56px->48px (User-Feedback 2026-07-22: wirkte auf Mobile
             überdimensioniert) + etwas dezenteres Glow — 48px bleibt über dem
             48px-Mindestmaß gängiger Touch-Ziel-Richtlinien. */
          right: calc(16px + env(safe-area-inset-right, 0px));
          bottom: calc(80px + env(safe-area-inset-bottom, 0px));
          width: 48px;
          height: 48px;
          box-shadow: 0 6px 16px rgba(2, 132, 199, 0.28), 0 2px 6px rgba(16, 24, 40, 0.12);
        }
        .hub-launcher-icon {
          font-size: 18px;
        }
        .hub-panel {
          right: 0;
          bottom: 0;
          width: 100vw;
          max-width: 100vw;
          /* A1 — echtes Vollbild. dvh statt vh: mit 100vh schiebt die eingeblendete
             Browserleiste die Eingabezeile aus dem Bild (100vh ist die Hoehe OHNE Leiste,
             der sichtbare Bereich ist kleiner). Der vh-Wert bleibt als Rueckfall fuer
             Browser ohne dvh stehen - sonst griffe die 70vh-Basisregel. */
          height: 100vh;
          height: 100dvh;
          max-height: 100vh;
          max-height: 100dvh;
          border-radius: 0;
          border: none;
          /* Vollbild beginnt bei y=0 — auf dem iPhone (viewport-fit=cover) UNTER der
             Statusleiste. Ohne diesen Abstand lag der X-Knopf bis zum 24.09.2026 unter
             Uhr/Akku und war nicht erreichbar. Token deckt auch die Android-Statusleiste. */
          box-sizing: border-box;
          padding-top: var(--safe-area-inset-top, 0px);
          padding-bottom: var(--safe-area-inset-bottom, 0px);
          /* Die Basisregel nimmt --clr-surface-100 = #fffffff0, also 94 % deckend. Auf
             Tablet/Desktop ist das ein gewollter Hauch Transparenz an einem kleinen
             angedockten Fenster; über die ganze Fläche eines Telefons schimmert dagegen
             der Planer durch die Antwort. Vollbild braucht eine deckende Fläche. */
          background: var(--clr-surface-200, #f8f9fa);
        }
      }

      /* Touch-Tablet (kein Telefon-Vollbild <=600px, kein Desktop-Maus-Pointer):
         das Panel nutzt deutlich mehr Breite/Hoehe und bekommt einen Tap-ausserhalb-
         Scrim. Trifft iPad/Android-Tablets in Hoch- UND Querformat (pointer: coarse). */
      @media (min-width: 601px) and (pointer: coarse) {
        .hub-backdrop {
          display: block;
          background: rgba(16, 24, 40, 0.32);
        }
        .hub-launcher {
          /* Über dem Scrim bright/tappbar halten — bleibt der persistente Toggle. */
          z-index: 8700;
        }
        /* Ist das Panel offen, blendet der Launcher auf Touch-Tablets AUS (Full-Takeover).
           Grund: Das Panel dockt unten rechts an (bottom:92px), sein Footer-Senden-Button
           und der Launcher (bottom:24px) landen in DERSELBEN rechten Ecke — der Launcher
           wird von der Panel-Ecke angeschnitten und steht direkt unter dem blauen
           Senden-Button (zwei blaue Kreise). Im offenen Zustand ist der Launcher ohnehin
           redundant: Schliessen uebernehmen das prominente Header-✕ und der Backdrop-Tap
           (Standard-Pattern fuer modale/kompakte Assistenten, analog zum Vollbild-Mobil-
           Modus). -> Eingabe-/Senden-Zeile bleibt frei, keine angeschnittene FAB. */
        .hub-launcher.is-open {
          display: none;
        }
        .hub-panel {
          /* Am Launcher angedockt (Best Practice fuer launcher-getriggerte Assistenten,
             Intercom/Material-Expanding-FAB): klappt unten rechts AUS DEM BUTTON heraus
             auf (Basis-Verankerung right:24px / bottom:92px + transform-origin bottom
             right bleiben), der Launcher daneben bleibt der Schliessen-Toggle in Daumen-
             reichweite. Breite bewusst auf die Chat-Inhaltsbreite abgestimmt (help-chat
             :host max-width 520px) -> Content fuellt das Panel, KEINE weisse Restflaeche;
             auf schmalen Tablets faellt die Breite auf calc(100vw - 48px) zurueck. */
          width: min(500px, calc(100vw - 48px));
          height: min(760px, calc(100vh - 132px - env(safe-area-inset-bottom, 0px)));
          max-height: calc(100vh - 108px - env(safe-area-inset-bottom, 0px));
          min-height: 480px;
        }
      }
    `,
  ],
})
export class HelpHubComponent {
  /** Statische Tab-Definitionen — feste Array-Referenzen (kein Getter, der
   *  bei jedem CD-Zyklus ein neues Array/Objekt erzeugt), damit xds-tabs'
   *  trackBy stabil bleibt. */
  private static readonly TAB_QA: XdsTab = { id: 'fragen', labelKey: 'HELP_HUB.TAB_QA', icon: 'fa-solid fa-comment-dots' };
  private static readonly TAB_SETUP: XdsTab = { id: 'einrichtung', labelKey: 'HELP_HUB.TAB_SETUP', icon: 'fa-solid fa-list-check' };
  private static readonly TABS_QA_SETUP: XdsTab[] = [HelpHubComponent.TAB_QA, HelpHubComponent.TAB_SETUP];
  private static readonly TABS_QA_ONLY: XdsTab[] = [HelpHubComponent.TAB_QA];

  /**
   * Höchstens zwei Reiter. Vom 09. bis 12.10.2026 stand dazwischen „Touren“; die Touren einer
   * Seite startet jetzt der Knopf „Tour“ im Seitenkopf, im Assistenten steht ein Hinweis.
   */
  protected tabsFor(showEinrichtungTab: boolean): XdsTab[] {
    return showEinrichtungTab ? HelpHubComponent.TABS_QA_SETUP : HelpHubComponent.TABS_QA_ONLY;
  }

  protected onTabChange(id: string): void {
    this.state.setTab(id as HubTab);
  }

  /**
   * In der Self-Service-Demo ist dieser Knopf der einzige Weg zum Assistenten
   * (der Einrichtungs-Reiter entfällt dort, siehe A5 oben). Ein Rettungsring
   * mit "Hilfe & Einrichtung öffnen" ist dort falsch beschriftet — er ist das
   * Symbol für "ich stecke fest", wird aber angetippt, wenn man die
   * Vorführung sehen will. Im Kundenkonto bleibt der Knopf unverändert, weil
   * er dort der einzige Hilfe-Einstieg ist.
   */
  protected readonly isDemo = isDemoSessionActive();

  constructor(
    public state: HelpHubStateService,
    public chat: HelpChatService,
    private preflight: TourPreflightService,
    private contextualTour: ContextualTourTriggerService,
    private router: Router,
  ) {}

  /** Badge-Variante nach Rest-Stand (neutral → warning ab ≤3 → danger bei 0). */
  protected quotaBadgeVariant(quota: DemoAiQuota): 'neutral' | 'warning' | 'danger' {
    if (quota.remaining <= 0) return 'danger';
    if (quota.remaining <= 3) return 'warning';
    return 'neutral';
  }

  /** Esc schliesst das offene Panel (Standard fuer nicht-modale Dialoge). */
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.state.isOpen) {
      this.state.close();
    }
  }

  protected ringDash(pct: number): string {
    const c = 2 * Math.PI * 24; // ~150.8 (Radius 24 → 56px-FAB)
    const filled = (Math.max(0, Math.min(100, pct)) / 100) * c;
    return `${filled} ${c}`;
  }

  /** Guide-Handoff aus dem Chat: Settings-Preflight, dann echte Tour starten. */
  protected onGuide(tourKey: string): void {
    const pre = this.preflight.preflightForTour(tourKey);
    if (!pre.ready && pre.deepLink) {
      // Fehlendes Settings-Haekchen: erst zur Einstellung fuehren.
      this.router.navigateByUrl(pre.deepLink.route);
      this.state.close();
      return;
    }
    // Voraussetzungen erfuellt: echte Tour (Spotlight + Backend-Step-Tracking).
    this.contextualTour.toggle(tourKey as TourFeature, false).catch(() => {
      /* Tour nicht startbar — leise ignorieren (Nudge-Fallback greift im Coordinator) */
    });
    this.state.close();
  }
}
